import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  FileText,
  Globe2,
  LineChart,
  Radar,
  ShieldCheck,
  Compass,
  ClipboardList,
  Activity,
  PieChart,
  CheckCircle2,
} from "lucide-react";
import { useI18n } from "@/i18n";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Kpi = ({ label, value, note, testId }) => (
  <div
    data-testid={testId}
    className="rounded-sm border border-slate-200 bg-white/95 px-5 py-4 shadow-sm backdrop-blur"
  >
    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C85A32]">
      {label}
    </div>
    <div className="mt-1 font-serif text-2xl font-bold text-[#0F2C59]">
      {value}
    </div>
    <div className="mt-1 text-xs text-slate-500">{note}</div>
  </div>
);

const whyIcons = [ShieldCheck, Globe2, BarChart3, LineChart];
const stepIcons = [Compass, ClipboardList, Activity, PieChart];

export const Home = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!name.trim() || name.trim().length < 2) nextErrors.name = t("home.form_error_name");
    if (!emailRegex.test(email.trim())) nextErrors.email = t("home.form_error_email");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitting(true);
    // Simulate submission and redirect
    setTimeout(() => {
      navigate("/thank_you");
    }, 300);
  };

  return (
    <div>
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-slate-200 bg-[#0F2C59] text-white"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url(/images/vaca-muerta.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2C59] via-[#0F2C59]/90 to-[#0F2C59]/60" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-sm border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C85A32]" />
              {t("home.hero_eyebrow")}
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              {t("home.hero_title")}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-200 lg:text-lg">
              {t("home.hero_intro_p1")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-300">
              {t("home.hero_intro_p2")}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#newsletter"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center rounded-sm bg-[#C85A32] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a94a28]"
              >
                {t("home.hero_cta_primary")}
              </a>
              <a
                href="#methodology"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center rounded-sm border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
              >
                {t("home.hero_cta_secondary")}
              </a>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Kpi
              testId="kpi-vaca-muerta"
              label={t("home.kpi_1_label")}
              value={t("home.kpi_1_value")}
              note={t("home.kpi_1_note")}
            />
            <Kpi
              testId="kpi-lithium"
              label={t("home.kpi_2_label")}
              value={t("home.kpi_2_value")}
              note={t("home.kpi_2_note")}
            />
            <Kpi
              testId="kpi-rigi"
              label={t("home.kpi_3_label")}
              value={t("home.kpi_3_value")}
              note={t("home.kpi_3_note")}
            />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" data-testid="why-section" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("home.why_eyebrow")}
            </div>
            <h2 className="font-serif text-2xl font-bold tracking-tight text-[#0F2C59] sm:text-3xl lg:text-4xl">
              {t("home.why_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i, idx) => {
              const Icon = whyIcons[idx];
              return (
                <div
                  key={i}
                  data-testid={`why-card-${i}`}
                  className="flex flex-col rounded-sm border border-slate-200 bg-[#FAFAFC] p-6"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-sm bg-[#0F2C59] text-white">
                    <Icon size={20} />
                  </div>
                  <h3 className="mb-2 font-serif text-lg font-semibold text-[#0F2C59]">
                    {t(`home.why_card_${i}_title`)}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-600">
                    {t(`home.why_card_${i}_desc`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" data-testid="sectors-section" className="border-b border-slate-200 bg-[#FAFAFC]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mb-12 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
                {t("home.sectors_eyebrow")}
              </div>
              <h2 className="font-serif text-2xl font-bold tracking-tight text-[#0F2C59] sm:text-3xl lg:text-4xl">
                {t("home.sectors_title")}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { img: "/images/vaca-muerta.jpg", i: 1, testId: "sector-vaca-muerta" },
              { img: "/images/salt-flats.jpg", i: 2, testId: "sector-lithium-triangle" },
              { img: "/images/mining.jpg", i: 3, testId: "sector-copper-mining" },
            ].map(({ img, i, testId }) => (
              <article
                key={i}
                data-testid={testId}
                className="overflow-hidden rounded-sm border border-slate-200 bg-white"
              >
                <div className="aspect-[16/10] w-full overflow-hidden bg-slate-200">
                  <img
                    src={img}
                    alt={t(`home.sector_${i}_title`)}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="mb-2 font-serif text-lg font-semibold text-[#0F2C59]">
                    {t(`home.sector_${i}_title`)}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-600">
                    {t(`home.sector_${i}_desc`)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section id="methodology" data-testid="methodology-section" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("home.how_eyebrow")}
            </div>
            <h2 className="font-serif text-2xl font-bold tracking-tight text-[#0F2C59] sm:text-3xl lg:text-4xl">
              {t("home.how_title")}
            </h2>
            <p className="mt-4 text-base text-slate-600">{t("home.how_intro")}</p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i, idx) => {
              const Icon = stepIcons[idx];
              return (
                <div
                  key={i}
                  data-testid={`method-step-${i}`}
                  className="relative flex flex-col rounded-sm border border-slate-200 bg-[#FAFAFC] p-6"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="font-serif text-3xl font-bold text-[#C85A32]">
                      0{i}
                    </span>
                    <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-slate-300 text-[#0F2C59]">
                      <Icon size={18} />
                    </div>
                  </div>
                  <h3 className="mb-2 font-serif text-lg font-semibold text-[#0F2C59]">
                    {t(`home.how_step_${i}_title`)}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-600">
                    {t(`home.how_step_${i}_desc`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" data-testid="transparency-section" className="border-b border-slate-200 bg-[#0F2C59] text-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("home.transparency_eyebrow")}
            </div>
            <h2 className="font-serif text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t("home.transparency_title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-200">
              {t("home.transparency_p1")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-300">
              {t("home.transparency_p2")}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="overflow-hidden rounded-sm border border-white/20">
              <div className="aspect-[16/9] w-full bg-slate-800">
                <img
                  src="/images/salt-flats-2.jpg"
                  alt="Salinas Argentina"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-200">
              {[1, 2, 3, 4].map((i) => (
                <li
                  key={i}
                  data-testid={`transparency-bullet-${i}`}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-[#C85A32]" />
                  <span>{t(`home.transparency_bullet_${i}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NEWSLETTER FORM */}
      <section id="newsletter" data-testid="newsletter-section" className="bg-[#FAFAFC]">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="rounded-sm border border-slate-200 bg-white p-8 sm:p-12">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("home.form_eyebrow")}
            </div>
            <h2 className="font-serif text-2xl font-bold tracking-tight text-[#0F2C59] sm:text-3xl">
              {t("home.form_title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {t("home.form_subtitle")}
            </p>

            <form
              onSubmit={handleSubmit}
              noValidate
              data-testid="newsletter-form"
              className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <div>
                <label
                  htmlFor="nl-name"
                  className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-700"
                >
                  {t("home.form_name_label")}
                </label>
                <input
                  id="nl-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("home.form_name_placeholder")}
                  data-testid="newsletter-input-name"
                  className="block w-full rounded-sm border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:border-[#0F2C59]"
                />
                {errors.name && (
                  <p
                    data-testid="newsletter-error-name"
                    className="mt-1 text-xs font-medium text-[#C85A32]"
                  >
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="nl-email"
                  className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-700"
                >
                  {t("home.form_email_label")}
                </label>
                <input
                  id="nl-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("home.form_email_placeholder")}
                  data-testid="newsletter-input-email"
                  className="block w-full rounded-sm border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:border-[#0F2C59]"
                />
                {errors.email && (
                  <p
                    data-testid="newsletter-error-email"
                    className="mt-1 text-xs font-medium text-[#C85A32]"
                  >
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-md text-xs leading-relaxed text-slate-500">
                  {t("home.form_consent")}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit-button"
                  className="inline-flex items-center justify-center rounded-sm bg-[#0F2C59] px-6 py-3 text-sm font-semibold text-white hover:bg-[#153970] disabled:opacity-70"
                >
                  {submitting ? t("home.form_submitting") : t("home.form_submit")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
